import shortid from 'shortid';

/**
 * Ask AI conversation history, persisted per restaurant in localStorage.
 *
 * Browser storage rather than the server: history here is a convenience for the
 * person who wrote the prompts, not a record anyone else needs, and keeping it
 * client-side avoids a collection, endpoints, and a retention policy for data
 * with no reader. The cost is that it is per-device and invisible to colleagues.
 */

export interface Message {
	role: 'user' | 'assistant';
	content: string;
	/** Absent on messages stored before timestamps were shown. */
	created?: number;
	summary?: string;
	proposal?: T.API.AIWebsiteProposal;
	/** 'expired' is set on load, where the outcome was never recorded. */
	settled?: 'applied' | 'undone' | 'failed' | 'expired';
	/**
	 * Path-to-value patch restoring what the change overwrote. Runtime only: a
	 * patch replayed in a later session could revert edits made in between, so it
	 * is not persisted and Undo does not survive a refresh.
	 */
	revert?: T.ObjectAny;
	/** Settings blocks the change wrote to, replayed so Undo rebuilds them too. */
	affected?: string[];
}

export interface Conversation {
	id: string;
	messages: Message[];
	updated: number;
}

/** Bounds the payload well below the ~5MB localStorage budget. */
const MAX_CONVERSATIONS = 20;
const MAX_MESSAGES = 60;

const key = (restaurantId: string): string => `cw-ai-chats:${restaurantId}`;

export const newConversation = (): Conversation => ({
	id: shortid.generate(),
	messages: [],
	updated: Date.now(),
});

/** The first thing asked, which is what the operator will recognise it by. */
export const title = (conversation: Conversation): string => {
	const first = conversation.messages.find(m => m.role === 'user' && !!m.content.trim());
	if (!first) return 'New chat';
	const text = first.content.trim().replace(/\s+/g, ' ');
	return text.length > 48 ? `${text.slice(0, 48)}...` : text;
};

/**
 * Only the readable record is kept: what was asked, what changed, and how it
 * ended. Proposals and revert patches are runtime state, and replaying either
 * against a site that has moved on since could undo work done in between.
 */
const forStorage = (conversations: Conversation[]): Conversation[] =>
	conversations.slice(0, MAX_CONVERSATIONS).map(c => ({
		id: c.id,
		updated: c.updated,
		messages: c.messages.slice(-MAX_MESSAGES).map(m => ({
			role: m.role,
			content: m.content,
			created: m.created,
			summary: m.summary,
			settled: m.proposal && !m.settled ? ('expired' as const) : m.settled,
		})),
	}));

const SETTLED = ['applied', 'undone', 'failed'];

/** 'kept' predates automatic apply and meant the same outcome. */
const settledFromStorage = (value: any, hadSummary: boolean): Message['settled'] => {
	if (value === 'kept') return 'applied';
	if (SETTLED.indexOf(value) !== -1) return value;
	return hadSummary ? 'expired' : undefined;
};

export const load = (restaurantId: string): Conversation[] => {
	try {
		const raw = window.localStorage.getItem(key(restaurantId));
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		// Anything shaped wrong is discarded rather than repaired: this is a cache,
		// and a partially understood entry is worth less than a clean slate.
		return parsed
			.filter(c => c && typeof c.id === 'string' && Array.isArray(c.messages))
			.map(c => ({
				id: c.id,
				updated: typeof c.updated === 'number' ? c.updated : 0,
				messages: c.messages
					.filter((m: any) => m && (m.role === 'user' || m.role === 'assistant'))
					.map((m: any) => ({
						role: m.role,
						content: typeof m.content === 'string' ? m.content : '',
						created: typeof m.created === 'number' ? m.created : undefined,
						summary: typeof m.summary === 'string' ? m.summary : undefined,
						settled: settledFromStorage(m.settled, typeof m.summary === 'string'),
					})),
			}));
	} catch (e) {
		// Storage can be unavailable (private mode, quota, disabled). History is
		// optional, so degrade to an in-memory session instead of failing.
		return [];
	}
};

export const save = (restaurantId: string, conversations: Conversation[]): void => {
	try {
		const withContent = conversations.filter(c => c.messages.length);
		if (!withContent.length) {
			window.localStorage.removeItem(key(restaurantId));
			return;
		}
		window.localStorage.setItem(key(restaurantId), JSON.stringify(forStorage(withContent)));
	} catch (e) {
		// Quota exceeded or storage disabled. Losing history is not worth breaking
		// the conversation the operator is having right now.
	}
};
